import { useEffect, useState } from "react";
import { CotizadorModelo, guardarArchivo, type IArchivoDescargado } from "../../../modelos/CotizadorNBS";
import { useCargando } from "../../../globales/contextos/cargandoContext";
import { useNotificaciones } from "../../../globales/contextos/notificationContext";
import { EnumTipoNotificacion } from "../../../globales/interfaces";
import ModalPrevisualizarPDF from "../../gestionArchivos/vista/modalPrevisualizarPDF";

/**
 * PDF de la Oferta Comercial: previsualizar (mismo modal de Gestión de archivos) o descargar.
 * Generarlo tarda unos segundos (Chromium en el back), así que "Descargar" dentro de la previsualización
 * guarda el mismo archivo que se está viendo, sin volver a pedirlo.
 */
export const usePdfCotizacion = () => {
    const { manejarCargando } = useCargando();
    const { mostrarNotificacion } = useNotificaciones();
    const [archivo, setArchivo] = useState<IArchivoDescargado | null>(null);
    const [urlArchivo, setUrlArchivo] = useState("");

    // El blob de la previsualización se libera al cerrarla o al salir de la pantalla.
    useEffect(() => () => { if (urlArchivo) URL.revokeObjectURL(urlArchivo); }, [urlArchivo]);

    const obtener = async (cotizacionId: string) => {
        try {
            manejarCargando(true, "Generando PDF...", false);
            return await new CotizadorModelo().obtenerPdfCotizacion(cotizacionId);
        } catch (error: any) {
            console.error("Error al generar el PDF", error);
            mostrarNotificacion(EnumTipoNotificacion.Error, "No se pudo generar el PDF", error?.mensajeBackend || "Intenta de nuevo en unos momentos.");
            return null;
        } finally {
            manejarCargando(false);
        }
    };

    const previsualizar = async (cotizacionId: string) => {
        const generado = await obtener(cotizacionId);
        if (!generado) return;
        setArchivo(generado);
        setUrlArchivo(URL.createObjectURL(generado.contenido));
    };

    const descargar = async (cotizacionId: string) => {
        const generado = await obtener(cotizacionId);
        if (generado) guardarArchivo(generado);
    };

    const cerrar = () => {
        setArchivo(null);
        setUrlArchivo("");
    };

    const modalPdf = (
        <ModalPrevisualizarPDF
            abierto={!!archivo}
            cerrar={cerrar}
            urlPDF={urlArchivo}
            nombreArchivo={archivo?.nombreArchivo ?? ""}
            descargarPDF={() => archivo && guardarArchivo(archivo)}
        />
    );

    return { previsualizar, descargar, modalPdf };
};
